import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ExternalLink, Clock, X } from 'lucide-react';
import {
  DraftSummary,
  listScheduled,
  scheduleDraft,
  unscheduleDraft,
  FinalRecipient,
} from '../../lib/api/notifications.js';
import {
  ScheduleSendPopover,
  formatSydneyShort,
} from './ScheduleSendPopover.js';

interface ScheduledCalendarViewProps {
  /** Operator filter context applied to the calendar query. */
  filters: {
    target_id?: string;
    entity_group?: 'coverage' | 'prospects' | 'majors' | 'picked' | 'all' | 'campaigns';
    q?: string;
  };
  onOpenDraft: (draftId: string) => void;
}

/**
 * Build YYYY-MM-DD (Sydney TZ) for a given local Date. Uses Intl with
 * timeZone 'Australia/Sydney' so it's machine-TZ-agnostic.
 */
function sydneyDateStr(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function sydneyTimeShort(utcIso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Australia/Sydney',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(utcIso));
}

/**
 * Today's Sydney-local date as a Date object positioned at 00:00 Sydney.
 * Returned as a UTC Date for arithmetic; format with `sydneyDateStr`.
 */
function sydneyToday(): Date {
  const s = sydneyDateStr(new Date());
  return new Date(`${s}T00:00:00Z`); // UTC midnight at Sydney's date — close enough for arithmetic at day granularity
}

function addDaysUtc(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 86400_000);
}

function startOfWeek(d: Date): Date {
  // Treat the date as Sydney-local. JS Date.getUTCDay() on a UTC-midnight
  // date returns the day-of-week we want for a Sydney-local YYYY-MM-DD.
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return addDaysUtc(d, mondayOffset);
}

function startOfMonth(d: Date): Date {
  const s = sydneyDateStr(d);
  const [y, m] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1));
}

function bucketByDay(items: DraftSummary[]): Map<string, DraftSummary[]> {
  const buckets = new Map<string, DraftSummary[]>();
  for (const it of items) {
    if (!it.scheduled_for) continue;
    const key = sydneyDateStr(new Date(it.scheduled_for));
    let arr = buckets.get(key);
    if (!arr) {
      arr = [];
      buckets.set(key, arr);
    }
    arr.push(it);
  }
  Array.from(buckets.values()).forEach((arr) => {
    arr.sort((a, b) => (a.scheduled_for ?? '').localeCompare(b.scheduled_for ?? ''));
  });
  return buckets;
}

export function ScheduledCalendarView({ filters, onOpenDraft }: ScheduledCalendarViewProps) {
  const [mode, setMode] = useState<'week' | 'month'>('week');
  const [anchor, setAnchor] = useState<Date>(() => sydneyToday());

  const { from, to } = useMemo(() => {
    if (mode === 'week') {
      const start = startOfWeek(anchor);
      return { from: sydneyDateStr(start), to: sydneyDateStr(addDaysUtc(start, 6)) };
    }
    const start = startOfMonth(anchor);
    // Render 6 rows of 7 days = 42 cells.
    const monthStart = startOfWeek(start);
    return { from: sydneyDateStr(monthStart), to: sydneyDateStr(addDaysUtc(monthStart, 41)) };
  }, [anchor, mode]);

  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications-scheduled', from, to, filters],
    queryFn: () => listScheduled({ from, to, ...filters }),
  });

  const bucketed = useMemo(() => bucketByDay(data?.items ?? []), [data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications-scheduled'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-queue-grouped'] });
  };

  const nav = (deltaDays: number) => setAnchor((a) => addDaysUtc(a, deltaDays));

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav(mode === 'week' ? -7 : -30)}
            className="p-1.5 rounded border border-grey-200 hover:bg-grey-50"
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setAnchor(sydneyToday())}
            className="px-3 py-1 text-xs border border-grey-200 rounded hover:bg-grey-50"
          >
            Today
          </button>
          <button
            onClick={() => nav(mode === 'week' ? 7 : 30)}
            className="p-1.5 rounded border border-grey-200 hover:bg-grey-50"
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="ml-2 text-sm font-semibold text-grey-700">
            {mode === 'week'
              ? `Week of ${formatRange(from, to)}`
              : new Intl.DateTimeFormat('en-AU', {
                  timeZone: 'Australia/Sydney',
                  month: 'long',
                  year: 'numeric',
                }).format(anchor)}
            <span className="text-grey-500 font-normal"> · Sydney</span>
          </span>
        </div>
        <div className="inline-flex rounded border border-grey-200 overflow-hidden">
          <button
            onClick={() => setMode('week')}
            className={`px-3 py-1 text-xs ${
              mode === 'week'
                ? 'bg-primary-04 text-white'
                : 'bg-surface text-grey-700 hover:bg-grey-50'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setMode('month')}
            className={`px-3 py-1 text-xs ${
              mode === 'month'
                ? 'bg-primary-04 text-white'
                : 'bg-surface text-grey-700 hover:bg-grey-50'
            }`}
          >
            Month
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-grey-500">Loading scheduled sends…</p>
      ) : mode === 'week' ? (
        <WeekGrid
          from={from}
          bucketed={bucketed}
          onOpenDraft={onOpenDraft}
          onMutated={invalidate}
        />
      ) : (
        <MonthGrid
          from={from}
          bucketed={bucketed}
          onClickDay={(date) => {
            setAnchor(new Date(`${date}T00:00:00Z`));
            setMode('week');
          }}
        />
      )}
    </div>
  );
}

function formatRange(from: string, to: string): string {
  const f = new Date(`${from}T00:00:00Z`);
  const t = new Date(`${to}T00:00:00Z`);
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', ...opts }).format(d);
  return `${fmt(f, { weekday: 'short', day: 'numeric', month: 'short' })} – ${fmt(t, { weekday: 'short', day: 'numeric', month: 'short' })}`;
}

/* -------------------------------------------------------------------- */
/* Week view                                                            */
/* -------------------------------------------------------------------- */

interface WeekGridProps {
  from: string;
  bucketed: Map<string, DraftSummary[]>;
  onOpenDraft: (draftId: string) => void;
  onMutated: () => void;
}

function WeekGrid({ from, bucketed, onOpenDraft, onMutated }: WeekGridProps) {
  const start = new Date(`${from}T00:00:00Z`);
  const days = Array.from({ length: 7 }, (_, i) => addDaysUtc(start, i));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-2">
      {days.map((d) => {
        const dateStr = sydneyDateStr(d);
        const drafts = bucketed.get(dateStr) ?? [];
        const isPopulated = drafts.length > 0;
        return (
          <div
            key={dateStr}
            className={`rounded border min-h-[200px] flex flex-col ${
              isPopulated
                ? 'bg-success-50/60 border-success-200'
                : 'bg-grey-50 border-grey-200'
            }`}
          >
            <div className={`px-2 py-1.5 border-b ${isPopulated ? 'border-success-200' : 'border-grey-200'} flex items-center justify-between`}>
              <div>
                <div className="text-[10px] uppercase text-grey-500 tracking-wide">
                  {new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', weekday: 'short' }).format(d)}
                </div>
                <div className="text-sm font-semibold text-grey-900">
                  {new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(d)}
                </div>
              </div>
              <span
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  isPopulated
                    ? 'bg-success-100 text-success-700'
                    : 'bg-grey-100 text-grey-500'
                }`}
              >
                {drafts.length} scheduled
              </span>
            </div>
            <div className="flex-1 p-1.5 space-y-1.5">
              {drafts.length === 0 ? (
                <div className="h-full flex items-center justify-center text-grey-300 text-xs">—</div>
              ) : (
                drafts.map((d) => (
                  <CalendarBlock
                    key={d.draft_id}
                    draft={d}
                    onOpen={() => onOpenDraft(d.draft_id)}
                    onMutated={onMutated}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Calendar block (single scheduled send)                               */
/* -------------------------------------------------------------------- */

interface CalendarBlockProps {
  draft: DraftSummary;
  onOpen: () => void;
  onMutated: () => void;
}

function CalendarBlock({ draft, onOpen, onMutated }: CalendarBlockProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const recipientsFinal: FinalRecipient[] = (draft.recipients ?? [])
    .filter((r) => r.verdict === 'included')
    .map((r) => ({ email: r.email, included: true }));

  const submitReschedule = async (utcIso: string) => {
    setBusy('schedule'); setError(null);
    try {
      // Reschedule reuses the same /schedule endpoint — UPSERT semantics
      // move the pipeline_jobs row's next_run_at.
      await scheduleDraft(draft.draft_id, {
        recipients_final: recipientsFinal,
        scheduled_for: utcIso,
      });
      setPickerOpen(false);
      onMutated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to reschedule');
    } finally {
      setBusy(null);
    }
  };

  const handleUnschedule = async () => {
    if (!confirm('Unschedule this send? The draft will move back to Approved (you can re-schedule any time).')) {
      return;
    }
    setBusy('unschedule'); setError(null);
    try {
      await unscheduleDraft(draft.draft_id);
      onMutated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to unschedule');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="group relative rounded border border-success-300 bg-surface p-1.5 text-xs hover:border-success-400">
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono font-semibold text-grey-900 tabular-nums">
          {draft.scheduled_for ? sydneyTimeShort(draft.scheduled_for) : ''}
        </span>
        {draft.entity_identifier && (
          <span className="font-mono text-[10px] text-grey-600">{draft.entity_identifier}</span>
        )}
      </div>
      <div className="text-[11px] font-medium text-grey-700 truncate">{draft.target_name}</div>
      <button
        type="button"
        onClick={onOpen}
        className="text-left text-[11px] text-grey-700 hover:text-primary-04 truncate block w-full mt-0.5"
        title={draft.article_title ?? undefined}
      >
        {draft.article_title || draft.rendered_subject}
      </button>
      {(draft.article_generation_timestamp || draft.created_at) && (
        <div className="text-[10px] text-grey-500 mt-0.5" title="Article generated at (Sydney)">
          Article: {formatSydneyShort(draft.article_generation_timestamp || draft.created_at)}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between text-[10px]">
        {draft.live_url ? (
          <a
            href={draft.live_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-0.5 text-primary-04 hover:text-primary-600 font-medium"
          >
            View article <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : (
          <span />
        )}
        <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPickerOpen((v) => !v); }}
            disabled={!!busy}
            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border border-grey-200 hover:bg-grey-50 text-grey-700 disabled:opacity-50"
          >
            <Clock className="h-2.5 w-2.5" />
            Reschedule
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleUnschedule(); }}
            disabled={!!busy}
            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded border border-danger-200 text-danger-600 hover:bg-danger-50 disabled:opacity-50"
          >
            <X className="h-2.5 w-2.5" />
            Unschedule
          </button>
        </div>
      </div>

      {pickerOpen && (
        <ScheduleSendPopover
          initialUtcIso={draft.scheduled_for}
          expiresAtUtcIso={draft.expires_at}
          submitting={busy === 'schedule'}
          onSubmit={submitReschedule}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {error && (
        <div className="mt-1 px-1 py-0.5 bg-danger-50 border border-danger-200 rounded text-[10px] text-danger-600">
          {error}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Month view                                                           */
/* -------------------------------------------------------------------- */

interface MonthGridProps {
  from: string;
  bucketed: Map<string, DraftSummary[]>;
  onClickDay: (dateStr: string) => void;
}

function MonthGrid({ from, bucketed, onClickDay }: MonthGridProps) {
  const start = new Date(`${from}T00:00:00Z`);
  const cells = Array.from({ length: 42 }, (_, i) => addDaysUtc(start, i));

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="text-[10px] uppercase text-grey-500 tracking-wide text-center py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d) => {
          const dateStr = sydneyDateStr(d);
          const count = bucketed.get(dateStr)?.length ?? 0;
          const isPopulated = count > 0;
          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => onClickDay(dateStr)}
              className={`min-h-[64px] rounded border text-left p-1.5 ${
                isPopulated
                  ? 'bg-success-50/60 border-success-200 hover:bg-success-100/60'
                  : 'bg-grey-50 border-grey-200 hover:bg-grey-100/60'
              }`}
            >
              <div className="text-xs font-semibold text-grey-900 tabular-nums">
                {new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', day: 'numeric' }).format(d)}
              </div>
              {isPopulated ? (
                <div className="text-[11px] mt-1 inline-flex px-1.5 py-0.5 rounded bg-success-100 text-success-700 font-medium">
                  {count} {count === 1 ? 'send' : 'sends'}
                </div>
              ) : (
                <div className="text-[11px] mt-1 text-grey-300">—</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
